import { useState, useEffect, useCallback, useMemo } from 'react';
import api, { getSocket } from '../api';
import { useAuth } from '../context/AuthContext';
import { useLeagueSettings } from '../context/LeagueSettingsContext';
import { useGameContext } from '../context/GameContext';
import { useCurrentWeek } from '../context/CurrentWeekContext';
import { useSeason } from '../context/SeasonContext';
import {
  generateAllWeeks,
  getSimpsonsName,
  mergeAndSortLocksForWeek,
  getByWeekVisibility,
  getByUserRowVisibility,
} from '../utils/lockOverviewUtils';

const useLockOverview = ({ season, leaderboardData, navigationRequest, onNavigationHandled }) => {
  const [viewMode, setViewMode] = useState('by-week');
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [lockData, setLockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [allActiveUsers, setAllActiveUsers] = useState([]);
  const [calendarData, setCalendarData] = useState([]);

  const { user } = useAuth();
  const { settings } = useLeagueSettings();
  const { locksCache, getMaxPicksForWeek } = useGameContext();
  const { currentWeek } = useCurrentWeek();
  const { effectiveSeason } = useSeason();

  const hasReachedMaxPicks = useCallback(() => {
    if (!currentWeek || !locksCache[currentWeek]) return false;

    const locksForWeek = locksCache[currentWeek];
    const userLocksCount = locksForWeek.filter(
      (lock) =>
        lock.userId === user?.id &&
        (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    return userLocksCount >= getMaxPicksForWeek(currentWeek);
  }, [currentWeek, locksCache, user?.id, getMaxPicksForWeek]);

  const hasUserLockedForWeek = useCallback(
    (weekNumber) => {
      if (!locksCache[weekNumber]) return false;

      const locksForWeek = locksCache[weekNumber];
      const userLocksCount = locksForWeek.filter(
        (lock) =>
          lock.userId === user?.id &&
          (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
      ).length;

      return userLocksCount >= getMaxPicksForWeek(weekNumber);
    },
    [locksCache, user?.id, getMaxPicksForWeek]
  );

  useEffect(() => {
    const fetchAvailableWeeks = async () => {
      try {
        const response = await api.fetchCalendar(season);
        if (response.data) {
          setCalendarData(response.data);
          const weeks = response.data
            .filter((week) => !week.hide_week)
            .map((week) => week.week)
            .sort((a, b) => a - b);
          setAvailableWeeks(weeks);
        }
      } catch (err) {
        console.error('Error fetching available weeks:', err);
      }
    };

    if (season) {
      fetchAvailableWeeks();
    }
  }, [season]);

  useEffect(() => {
    const fetchAllActiveUsers = async () => {
      try {
        const response = await api.getActiveUsers();
        if (response.data && Array.isArray(response.data)) {
          setAllActiveUsers(response.data);
        }
      } catch (err) {
        console.error('Error fetching active users:', err);
        setAllActiveUsers([]);
      }
    };

    fetchAllActiveUsers();
  }, []);

  useEffect(() => {
    setSelectedWeek(currentWeek || 1);

    const fetchActiveUsers = async () => {
      try {
        const response = await api.getActiveUsers();
        if (response.data && Array.isArray(response.data)) {
          setAvailableUsers(response.data);
          if (response.data.length > 0) {
            setSelectedUser((prev) => {
              if (prev) return prev;
              const currentUser = response.data.find((u) => u === user?.username);
              return currentUser || response.data[0];
            });
          }
        }
      } catch (err) {
        if (leaderboardData?.users) {
          const usernames = leaderboardData.users.map((u) => u.username);
          setAvailableUsers(usernames);
          if (usernames.length > 0) {
            setSelectedUser((prev) => {
              if (prev) return prev;
              const currentUser = leaderboardData.users.find((u) => u.username === user?.username);
              return currentUser ? currentUser.username : usernames[0];
            });
          }
        }
      }
    };

    fetchActiveUsers();
  }, [leaderboardData, user?.username, currentWeek]);

  const fetchLockData = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      try {
        if (viewMode === 'by-week' && selectedWeek) {
          const response = await api.getLockOverview(season, selectedWeek, '', viewMode);
          setLockData(response.data);
        } else if (viewMode === 'by-user' && selectedUser) {
          const response = await api.getLockOverview(season, selectedWeek, selectedUser, viewMode);
          setLockData(response.data);
        }
      } catch (err) {
        console.error('Error fetching lock overview data:', err);
        setError('Failed to load lock overview data');
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [season, selectedWeek, selectedUser, viewMode]
  );

  useEffect(() => {
    fetchLockData(false);
  }, [fetchLockData]);

  useEffect(() => {
    if (!navigationRequest) return;

    if (navigationRequest.viewMode === 'by-user' && navigationRequest.username) {
      setViewMode('by-user');
      setSelectedUser(navigationRequest.username);
    } else if (navigationRequest.viewMode === 'by-week' && navigationRequest.week) {
      setViewMode('by-week');
      setSelectedWeek(navigationRequest.week);
    }

    onNavigationHandled?.();
  }, [navigationRequest, onNavigationHandled]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;

    let refreshTimer;
    const handleLiveUpdates = () => {
      if (season !== effectiveSeason) return;
      if (viewMode === 'by-week' && selectedWeek !== currentWeek) return;

      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        fetchLockData(true);
      }, 500);
    };

    socket.on('live_game_updates', handleLiveUpdates);
    return () => {
      clearTimeout(refreshTimer);
      socket.off('live_game_updates', handleLiveUpdates);
    };
  }, [season, effectiveSeason, selectedWeek, currentWeek, viewMode, fetchLockData]);

  const locksForWeek = useMemo(
    () =>
      mergeAndSortLocksForWeek({
        lockData,
        allActiveUsers,
        leaderboardData,
        selectedWeek,
        currentWeek,
        settings,
        hasReachedMaxPicks: hasReachedMaxPicks(),
      }),
    [
      lockData,
      allActiveUsers,
      leaderboardData,
      selectedWeek,
      currentWeek,
      settings,
      hasReachedMaxPicks,
    ]
  );

  const getLockForWeek = useCallback(
    (week, pickNumber = 1) => {
      if (!lockData?.weeklyLocks) return null;
      return lockData.weeklyLocks.find(
        (lock) =>
          lock.week === week &&
          (lock.pickNumber === pickNumber || (!lock.pickNumber && pickNumber === 1))
      );
    },
    [lockData]
  );

  const allWeeks = useMemo(() => generateAllWeeks(), []);

  const showStaleDataMessage =
    !hasReachedMaxPicks() &&
    !settings.show_locks &&
    !settings.show_lockers &&
    season === effectiveSeason;

  const previousWeek = currentWeek > 1 ? currentWeek - 1 : 1;

  const isUsernameClickable = useCallback(
    (lock) => {
      const { shouldBlurUser, shouldBlurEverything } = getByWeekVisibility({
        lock,
        selectedWeek,
        currentWeek,
        season,
        effectiveSeason,
        settings,
        hasReachedMaxPicks: hasReachedMaxPicks(),
        hasUserLockedForWeek,
      });
      return !shouldBlurUser && !shouldBlurEverything;
    },
    [
      selectedWeek,
      currentWeek,
      season,
      effectiveSeason,
      settings,
      hasReachedMaxPicks,
      hasUserLockedForWeek,
    ]
  );

  const getWeekVisibility = useCallback(
    (lock) =>
      getByWeekVisibility({
        lock,
        selectedWeek,
        currentWeek,
        season,
        effectiveSeason,
        settings,
        hasReachedMaxPicks: hasReachedMaxPicks(),
        hasUserLockedForWeek,
      }),
    [
      selectedWeek,
      currentWeek,
      season,
      effectiveSeason,
      settings,
      hasReachedMaxPicks,
      hasUserLockedForWeek,
    ]
  );

  const getUserWeekVisibility = useCallback(
    (weekNumber) =>
      getByUserRowVisibility({
        weekNumber,
        currentWeek,
        season,
        effectiveSeason,
        settings,
        hasUserLockedForWeek,
      }),
    [currentWeek, season, effectiveSeason, settings, hasUserLockedForWeek]
  );

  const handleUsernameClick = useCallback((username) => {
    setViewMode('by-user');
    setSelectedUser(username);
  }, []);

  const handleWeekClick = useCallback((week) => {
    setViewMode('by-week');
    setSelectedWeek(week);
  }, []);

  return {
    viewMode,
    setViewMode,
    selectedWeek,
    setSelectedWeek,
    selectedUser,
    setSelectedUser,
    lockData,
    loading,
    error,
    availableWeeks,
    availableUsers,
    calendarData,
    currentWeek,
    effectiveSeason,
    settings,
    locksForWeek,
    allWeeks,
    getLockForWeek,
    getWeekVisibility,
    getUserWeekVisibility,
    isUsernameClickable,
    handleUsernameClick,
    handleWeekClick,
    showStaleDataMessage,
    previousWeek,
    getSimpsonsName,
    hasReachedMaxPicks,
  };
};

export default useLockOverview;
